import { useUser } from "@/components/hooks/useUser";
import Providers from "@/components/Providers";
import { isAuthRecoveryEnabled } from "@/lib/posthog/auth-recovery-flag";
import { registerCustomSerializers } from "@/lib/server";
import { cn } from "@/lib/utils";
import { clashGrotesk, dmSans, herrVonMuellerhoff, jakartaSans, laBelleAurore } from "@/styles/fonts";

import "@/styles/globals.css";

import { useEffect } from "react";
import Head from "next/head";

import * as Sentry from "@sentry/browser";
import { DefaultSeo } from "next-seo";
import NextTopLoader from "nextjs-toploader";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { Toaster } from "react-hot-toast";

registerCustomSerializers();

export default function App({ Component, pageProps }) {
  const getLayout = Component.getLayout ?? ((page) => page);
  // Use static property on page component to determine if Clerk should be skipped
  // This avoids hydration mismatches that occur when using useRouter() in providers
  const skipClerk = Component.skipClerk === true;

  return (
    <div
      className={cn(
        "h-full font-sans",
        jakartaSans.variable,
        clashGrotesk.variable,
        laBelleAurore.variable,
        dmSans.variable,
        herrVonMuellerhoff.variable
      )}
      style={{
        fontFamily: "var(--font-jakarta-sans, ui-sans-serif, system-ui, sans-serif)",
      }}
    >
      <NuqsAdapter>
        <Providers skipClerk={skipClerk} {...pageProps}>
          <DefaultSeo
            title="AI-Powered CRM & Marketing for Home Service Pros | Rebolt"
            description="Rebolt replaces 10 tools with one AI-powered platform for home service pros."
            openGraph={{
              type: "website",
              url: "https://rebolthq.com",
              title: "AI-Powered CRM & Marketing for Home Service Pros | Rebolt",
              description: "Rebolt replaces 10 tools with one AI-powered platform for home service pros.",
            }}
            twitter={{
              site: "@getrebolt",
              cardType: "summary_large_image",
            }}
          />
          <Head>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link
              rel="icon"
              key="favicon"
              href={process.env.NODE_ENV === "development" ? "/favicon/favicon-dev.svg" : "/favicon.svg?v=2"}
            />
          </Head>
          <NextTopLoader color="#FF8241" />

          {/* InnerApp uses useUser() which requires Clerk - skip for routes without Clerk */}
          {getLayout(
            skipClerk ? <Component {...pageProps} /> : <InnerApp Component={Component} pageProps={pageProps} />
          )}

          <Toaster position="bottom-center" />
        </Providers>
      </NuqsAdapter>
    </div>
  );
}

// Have an inner app component that's within the top level provider so we have access to the user
function InnerApp({ Component, pageProps }) {
  const { user, session } = useUser();
  useEffect(() => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: userEmail,
        fullName: user.fullName,
      });
    }

    // Proactively touch the Clerk session when the tab becomes visible again,
    // so the cookie poller catches up before SWR's revalidate-on-focus hooks
    // fire. clerk-js's AuthCookieService already refreshes the token on
    // `focus`, so we deliberately do NOT add a focus listener (it would double
    // the FAPI round-trips per refocus) — but clerk-js does not listen to
    // `visibilitychange`, which covers refocus patterns where `focus` doesn't
    // fire (DevTools stealing focus, some alt-tab flows). The axios
    // interceptor in lib/utils handles the rare 401 that still slips through.
    if (!session) return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // Part of the authRecoveryInterceptor rollout — checked at event time so
      // toggling the kill switch takes effect without re-binding listeners.
      if (!isAuthRecoveryEnabled()) return;
      // Fire-and-forget: the axios interceptor in lib/utils is the real safety
      // net for a stale session, so a rejected touch (Clerk offline / blip)
      // must not surface as an unhandled rejection on every refocus.
      session?.touch?.().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, session]);

  return <Component {...pageProps} />;
}
